/**
 * PromptCanvasRenderer — learner-facing renderer for prompt_canvas segments.
 *
 * Renders a worked-example reference canvas with:
 *   • A header showing title, optional topic summary, and difficulty badge
 *   • A numbered steps section (prompt / code / concept blocks with copy buttons)
 *   • An optional key-insight callout
 *   • A footer with "Save to Library" and "Mark as Complete" actions
 *
 * Props:
 *   segment   — the segment object from TutorialPlayer (segment_type === 'prompt_canvas')
 *   onComplete — optional callback; called when the learner clicks "Mark as Complete"
 */
import React, { useState, useMemo, useEffect } from 'react';
import CopyButton from '@shared/components/CopyButton/CopyButton';
import { KeyInsightCallout } from '@shared/components/Feedback';

const AI_TOOLS = [
  { id: 'claude',  label: 'Claude',  icon: '🟠', color: '#d97706', url: (p) => `https://claude.ai/new?q=${encodeURIComponent(p)}` },
  { id: 'chatgpt', label: 'ChatGPT', icon: '🟢', color: '#10a37f', url: (p) => `https://chat.openai.com/?q=${encodeURIComponent(p)}` },
  { id: 'gemini',  label: 'Gemini',  icon: '🔵', color: '#1a73e8', url: (p) => `https://gemini.google.com/?q=${encodeURIComponent(p)}` },
];

const URL_PATTERN = /(https?:\/\/[^\s)<>]+)/g;

function LinkifiedText({ text }) {
  if (!text) return null;
  const parts = text.split(URL_PATTERN);
  return parts.map((part, i) => {
    const isUrl = /^https?:\/\//.test(part);
    return isUrl ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
         style={{ color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-all' }}>
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    );
  });
}

const DIFFICULTY_PILL = {
  beginner:     { emoji: '🟢', bg: '#dcfce7', color: '#166534' },
  intermediate: { emoji: '🟡', bg: '#fef3c7', color: '#92400e' },
  advanced:     { emoji: '🔴', bg: '#fee2e2', color: '#991b1b' },
};

export default function PromptCanvasRenderer({ segment, onComplete, draft = {}, onDraftChange }) {
  const [completed, setCompleted] = useState(!!draft?.completed);
  const [saved, setSaved]         = useState(false);
  const [saveError, setSaveError] = useState('');
  const [selectedTool, setSelectedTool] = useState(draft?.selectedTool || 'claude');
  const [submission, setSubmission] = useState(draft?.submission || '');
  const [submissionSaved, setSubmissionSaved] = useState(!!draft?.submission);

  // Resolve fields — support both flat and scene_data-nested shapes
  const title        = segment.title        || segment.scene_data?.title        || 'Prompt Canvas';
  const topicSummary = segment.topic_summary || segment.scene_data?.topic_summary || '';
  const difficulty   = segment.difficulty   || segment.scene_data?.difficulty   || null;
  const keyInsight   = segment.key_insight  || segment.scene_data?.key_insight  || '';
  const saveable     = (segment.saveable    ?? segment.scene_data?.saveable)    ?? true;
  const rawSteps     = Array.isArray(segment.steps)
    ? segment.steps
    : Array.isArray(segment.scene_data?.steps)
    ? segment.scene_data.steps
    : [];

  // Track editable prompt-type steps in local state (restored from draft)
  const [stepEdits, setStepEdits] = useState(() => (
    draft?.stepEdits && typeof draft.stepEdits === 'object' ? { ...draft.stepEdits } : {}
  ));

  useEffect(() => {
    if (draft?.submission != null && draft.submission !== submission) {
      setSubmission(draft.submission);
      if (draft.submission) setSubmissionSaved(true);
    }
    if (draft?.stepEdits && typeof draft.stepEdits === 'object') {
      setStepEdits((prev) => (
        JSON.stringify(prev) === JSON.stringify(draft.stepEdits) ? prev : { ...draft.stepEdits }
      ));
    }
    if (draft?.selectedTool) setSelectedTool(draft.selectedTool);
    if (draft?.completed != null) setCompleted(!!draft.completed);
  }, [draft?.submission, draft?.stepEdits, draft?.selectedTool, draft?.completed]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistDraft = (patch) => {
    onDraftChange?.({
      kind: 'prompt_canvas',
      submission,
      stepEdits,
      selectedTool,
      completed,
      ...patch,
    });
  };

  const steps = rawSteps.map((s, i) => stepEdits[i] !== undefined ? { ...s, content: stepEdits[i] } : s);

  const handleMarkComplete = () => {
    if (completed) {
      setCompleted(false);
      return;
    }
    setCompleted(true);
    if (onComplete) onComplete();
  };

  const handleSave = async () => {
    const API_BASE = process.env.REACT_APP_SCENARIO_OS_API || '/scenario-os';
    const token = localStorage.getItem('scenario_os_token');
    const segId = segment.segment_id || segment.id || 'unknown';
    const domain = segment.domain || segment.scene_data?.domain || 'general';
    try {
      const res = await fetch(`${API_BASE}/curriculum/resources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          domain,
          resource_type: 'prompt_canvas',
          resource_url: `app://canvas/${segId}`,
          title,
          source_name: 'Prompt Canvas',
          topic_summary: topicSummary,
          difficulty,
          steps,
          key_insight: keyInsight,
          module_id: segment.module_id || segment.scene_data?.module_id || null,
        }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setSaved(true);
      setSaveError('');
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setSaveError(err.message || 'Save failed');
      setTimeout(() => setSaveError(''), 3000);
    }
  };

  const diffPill = difficulty ? DIFFICULTY_PILL[difficulty] : null;

  return (
    <div style={containerStyle}>

      {/* ── Header ── */}
      <div style={headerStyle}>
        <div style={headerTopStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
            <span style={{ fontSize: 28 }}>📋</span>
            <h2 style={titleStyle}>{title}</h2>
          </div>
          {diffPill && (
            <span style={{ ...diffPillStyle, background: diffPill.bg, color: diffPill.color }}>
              {diffPill.emoji} {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
            </span>
          )}
        </div>
        {topicSummary && (
          <p style={topicSummaryStyle}>{topicSummary}</p>
        )}
      </div>

      {/* ── Steps ── */}
      {steps.length > 0 && (
        <div style={stepsContainerStyle}>
          {steps.map((step, idx) => {
            const isConcept = step.type === 'concept';
            const isPrompt = step.type === 'prompt';
            return (
              <div key={idx} style={stepWrapStyle}>
                <div style={stepLabelRowStyle}>
                  <span style={stepNumberStyle}>{idx + 1}.</span>
                  <span style={stepLabelStyle}>{step.label || `Step ${idx + 1}`}</span>
                  {isPrompt && <span style={{ fontSize: 11, color: '#3b82f6', marginLeft: 8 }}>✎ editable</span>}
                </div>
                <div style={{ position: 'relative' }}>
                  {isPrompt ? (
                    <textarea
                      value={step.content || ''}
                      onChange={(e) => {
                        const next = { ...stepEdits, [idx]: e.target.value };
                        setStepEdits(next);
                        persistDraft({ stepEdits: next });
                      }}
                      style={promptTextareaStyle}
                      rows={Math.max(6, (step.content || '').split('\n').length + 2)}
                    />
                  ) : isConcept ? (
                    <pre style={conceptBlockStyle}>
                      <code style={{ fontFamily: 'inherit', fontSize: 'inherit' }}>
                        <LinkifiedText text={step.content || ''} />
                      </code>
                    </pre>
                  ) : (
                    <pre style={codeBlockStyle}>
                      <code style={{ fontFamily: 'inherit', fontSize: 'inherit' }}>
                        {step.content || ''}
                      </code>
                    </pre>
                  )}
                  {step.copy_enabled && (
                    <div style={copyButtonWrapStyle}>
                      <CopyButton
                        text={step.content || ''}
                        size="small"
                        variant="button"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {steps.length === 0 && (
        <div style={emptyStepsStyle}>
          No steps authored yet. A coach needs to add <code>steps</code> to this segment.
        </div>
      )}

      {/* ── AI Tool Launcher ── */}
      {steps.length > 0 && (
        <div style={toolLauncherContainerStyle}>
          <div style={toolLauncherHeaderStyle}>
            <span style={{ fontSize: 20 }}>⚗️</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Try it with an AI Tool</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                Copy a prompt above, open your preferred AI tool, paste the prompt, and compare results.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {AI_TOOLS.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedTool(t.id)}
                style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.12s',
                  border: selectedTool === t.id ? `2px solid ${t.color}` : '2px solid transparent',
                  background: selectedTool === t.id ? '#fff' : '#e2e8f0',
                  color: selectedTool === t.id ? t.color : '#64748b',
                }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              const tool = AI_TOOLS.find((t) => t.id === selectedTool) || AI_TOOLS[0];
              const promptText = steps
                .filter((s) => s.type === 'prompt')
                .map((s) => s.content || '')
                .join('\n\n')
                .trim();
              window.open(tool.url(promptText), '_blank', 'noopener,noreferrer');
            }}
            style={{
              width: '100%', padding: '10px 16px',
              background: (AI_TOOLS.find((t) => t.id === selectedTool) || AI_TOOLS[0]).color,
              color: '#fff', border: 'none',
              borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Open {(AI_TOOLS.find((t) => t.id === selectedTool) || AI_TOOLS[0]).label} ↗
          </button>
        </div>
      )}

      {/* ── Submission Area ── */}
      {steps.length > 0 && (
        <div style={submissionContainerStyle}>
          <div style={{ fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, color: '#475569', marginBottom: 8 }}>
            Paste your results & observations
          </div>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 0, marginBottom: 10, lineHeight: 1.5 }}>
            After running the prompts in your AI tool, paste the output below along with any observations about what worked well and what didn't.
          </p>
          <textarea
            value={submission}
            onChange={(e) => {
              setSubmission(e.target.value);
              setSubmissionSaved(true);
              persistDraft({ submission: e.target.value });
            }}
            placeholder="Paste the AI's response here. Add your observations about the results — what surprised you? What would you change?"
            rows={6}
            style={submissionTextareaStyle}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <span style={{ fontSize: 12, color: submission.trim() ? '#059669' : '#94a3b8' }}>
              {submission.trim() ? `${submission.trim().split(/\s+/).length} words` : 'No response yet'}
            </span>
            {submissionSaved && (
              <span style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>Response saved</span>
            )}
          </div>
        </div>
      )}

      {/* ── Key Insight ── */}
      {keyInsight && (
        <div style={{ padding: '0 24px' }}>
          <KeyInsightCallout variant="insight" title="Key Insight">
            {keyInsight}
          </KeyInsightCallout>
        </div>
      )}

      {/* ── Footer ── */}
      <div style={footerStyle}>
        {saveable && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <button
              onClick={handleSave}
              style={saved ? savedBtnStyle : saveBtnStyle}
              disabled={saved}
            >
              {saved ? '✓ Saved to your library' : '💾 Save to My Canvas Library'}
            </button>
            {saveError && <span style={{ fontSize: 12, color: '#dc2626' }}>{saveError}</span>}
          </div>
        )}
        <button
          onClick={handleMarkComplete}
          style={completeBtnStyle(completed)}
        >
          {completed ? '✓ Marked Complete (click to undo)' : '✓ Mark as Complete'}
        </button>
      </div>

    </div>
  );
}

// ─── styles ─────────────────────────────────────────────────────────────────

const containerStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  overflow: 'hidden',
  background: '#fff',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const headerStyle = {
  padding: '20px 24px 16px',
  borderBottom: '1px solid #f1f5f9',
  background: '#fafbfc',
};

const headerTopStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 10,
  marginBottom: 6,
};

const titleStyle = {
  margin: 0,
  fontSize: 18,
  fontWeight: 700,
  color: '#0f172a',
  lineHeight: 1.3,
};

const topicSummaryStyle = {
  margin: '4px 0 0',
  fontSize: 14,
  color: '#475569',
  lineHeight: 1.5,
};

const diffPillStyle = {
  fontSize: 12,
  fontWeight: 600,
  padding: '3px 10px',
  borderRadius: 999,
  flexShrink: 0,
};

const stepsContainerStyle = {
  padding: '20px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
};

const stepWrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const stepLabelRowStyle = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 6,
};

const stepNumberStyle = {
  fontSize: 13,
  fontWeight: 700,
  color: '#64748b',
  minWidth: 20,
};

const stepLabelStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: '#1e293b',
};

const codeBlockStyle = {
  background: '#1e1e1e',
  color: '#d4d4d4',
  borderRadius: 8,
  padding: '14px 16px',
  fontSize: 13,
  lineHeight: 1.65,
  fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", Consolas, monospace',
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: 0,
};

const conceptBlockStyle = {
  ...codeBlockStyle,
  background: '#f8fafc',
  color: '#0f172a',
  border: '1px solid #e2e8f0',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const copyButtonWrapStyle = {
  position: 'absolute',
  top: 8,
  right: 8,
};

const emptyStepsStyle = {
  padding: '16px 24px',
  fontSize: 13,
  color: '#94a3b8',
};

const footerStyle = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '16px 24px',
  borderTop: '1px solid #f1f5f9',
  background: '#fafbfc',
};

const saveBtnStyle = {
  background: '#fff',
  color: '#374151',
  border: '1px solid #d1d5db',
  padding: '9px 16px',
  borderRadius: 7,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const savedBtnStyle = {
  ...saveBtnStyle,
  background: '#dcfce7',
  color: '#166534',
  border: '1px solid #86efac',
  cursor: 'default',
};

const promptTextareaStyle = {
  width: '100%',
  padding: '14px 16px',
  fontSize: 13,
  lineHeight: 1.65,
  fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", Consolas, monospace',
  borderRadius: 8,
  border: '2px solid #3b82f6',
  background: '#f0f9ff',
  color: '#0f172a',
  resize: 'vertical',
  boxSizing: 'border-box',
  whiteSpace: 'pre-wrap',
};

const toolLauncherContainerStyle = {
  margin: '0 24px 20px',
  padding: 16,
  background: '#f1f5f9',
  borderRadius: 10,
};

const toolLauncherHeaderStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  marginBottom: 12,
};

const submissionContainerStyle = {
  margin: '0 24px 20px',
  padding: 16,
  background: '#fafbfc',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
};

const submissionTextareaStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: 'inherit',
  lineHeight: 1.6,
  resize: 'vertical',
  boxSizing: 'border-box',
  background: '#fff',
  color: '#0f172a',
  minHeight: 100,
};

const completeBtnStyle = (done) => ({
  background: done ? '#dcfce7' : '#3b82f6',
  color: done ? '#166534' : '#fff',
  border: `1px solid ${done ? '#86efac' : '#3b82f6'}`,
  padding: '9px 18px',
  borderRadius: 7,
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
});
